import { QUESTION_TYPE } from '../../fetchSurvey/constants/questionTypes';
import type { SurveyPage, SurveyQuestion } from '../../fetchSurvey/types';
import type { SurveyAnswers } from '../../submitSurvey/types';
import type { CustomFieldValueMap } from '../../surveyLogic/types/customFieldValues';
import type { SurveyPaginationRefs } from '../../surveyPagination/types';
import { buildLogicContext } from '../../surveyLogic/context/buildLogicContext';
import type { LogicContext } from '../../surveyLogic/types';
import { getVisibleQuestions } from '../../surveyLogic/visibility';
import { isQuestionAnswered } from '../../surveyPagination/utils/validation';
import { calculateProgressPercentage } from './progress';
import {
  simulateActivePath,
  getActivePathQuestionIds,
  countAnswerableOnPath,
  countEarnedOnPath,
} from './activePath';

function isDisplayOnly(question: SurveyQuestion): boolean {
  return question.type === QUESTION_TYPE.TEXT_AND_MEDIA;
}

export function getAnswerableQuestionsOnPage(
  page: SurveyPage,
  context?: LogicContext
): SurveyQuestion[] {
  const base = (page.questions ?? []).filter(q => !isDisplayOnly(q));
  if (!context) {
    return base;
  }
  return getVisibleQuestions(base, context);
}

export function getAnswerableQuestionIdsOnPage(
  page: SurveyPage,
  context?: LogicContext
): string[] {
  return getAnswerableQuestionsOnPage(page, context).map(q => q.id);
}

export function earnQuestion(refs: SurveyPaginationRefs, questionId: string): void {
  refs.earnedQuestionIds.add(questionId);
}

export function earnPageOnVisit(
  refs: SurveyPaginationRefs,
  page: SurveyPage | undefined,
  values: SurveyAnswers,
  allQuestions?: SurveyQuestion[],
  customFieldValues: CustomFieldValueMap = {}
): void {
  if (!page) return;

  const context = allQuestions
    ? buildLogicContext(values, allQuestions, customFieldValues)
    : undefined;
  getAnswerableQuestionsOnPage(page, context).forEach(question => {
    if (!question.required) {
      earnQuestion(refs, question.id);
      return;
    }
    if (isQuestionAnswered(question, values, allQuestions, customFieldValues)) {
      earnQuestion(refs, question.id);
    }
  });
}

export function computeEarnedProgress(
  pages: SurveyPage[],
  values: SurveyAnswers,
  allQuestions: SurveyQuestion[],
  earnedQuestionIds: ReadonlySet<string>,
  customFieldValues: CustomFieldValueMap = {}
): {
  totalQuestionsCount: number;
  answeredQuestionsCount: number;
  progressPercentage: number;
  isComplete: boolean;
} {
  const pathPages = simulateActivePath(pages, values, allQuestions, customFieldValues);
  const pathQuestionIds = getActivePathQuestionIds(
    pages,
    pathPages,
    values,
    allQuestions,
    customFieldValues
  );
  const totalQuestionsCount = countAnswerableOnPath(
    pages,
    pathPages,
    values,
    allQuestions,
    customFieldValues
  );
  const answeredQuestionsCount = countEarnedOnPath(earnedQuestionIds, pathQuestionIds);
  const progressPercentage = calculateProgressPercentage(answeredQuestionsCount, totalQuestionsCount);
  const isComplete =
    totalQuestionsCount > 0 && answeredQuestionsCount >= totalQuestionsCount;

  return {
    totalQuestionsCount,
    answeredQuestionsCount,
    progressPercentage,
    isComplete,
  };
}
